/**
 * Custom hook for managing Trello integration state
 */

import { useState, useEffect, useCallback } from 'react';
import { trelloService } from '../services/trelloService';
import { useAuth } from '../contexts/AuthContext';
import { Lead } from '../types/Lead';
import { SendToTrelloRequest } from '../types/Trello';

interface UseTrelloIntegrationReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  isSent: boolean;
  error: string | null;
  sendToTrello: (lead: Lead, listId: string) => Promise<void>;
  clearError: () => void;
}

export const useTrelloIntegration = (leadId?: string): UseTrelloIntegrationReturn => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Check authentication status and if lead was already sent
  useEffect(() => {
    const checkStatus = async () => {
      const authState = trelloService.getAuthState();
      setIsAuthenticated(authState.isAuthenticated);

      if (authState.isAuthenticated && leadId) {
        const sentStatus = await trelloService.isLeadSentToTrello(leadId);
        setIsSent(sentStatus);
      }
    };

    checkStatus();
  }, [leadId, refreshKey]);

  const sendToTrello = useCallback(async (lead: Lead, listId: string) => {
    if (!user || !lead.id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Check if user is authenticated
      if (!isAuthenticated) {
        await trelloService.authenticate();
        setIsAuthenticated(true);
        return; // Authentication redirects, so return here
      }

      // Check if we have a list ID
      if (!listId) {
        throw new Error('Trello list not configured');
      }

      // Prepare lead data for Trello
      const request: SendToTrelloRequest = {
        leadId: lead.id,
        userUid: user.uid,
        leadData: {
          companyName: lead.metadata.companyName,
          sector: lead.metadata.sector,
          companySize: lead.metadata.companySize,
          location: `${lead.metadata.hqCity}, ${lead.metadata.hqCountry}`,
          averageScore: lead.processed.averageScore,
          overallScore: lead.overallScore,
          topContacts: lead.topContacts,
          metrics: lead.metrics
        }
      };

      // Send lead to Trello
      await trelloService.sendLeadToTrello(request, listId);

      // Update state immediately to reflect successful send
      setIsSent(true);

      // Trigger a refresh to ensure consistent state
      setRefreshKey(prev => prev + 1);

      console.log(`✅ Successfully sent ${lead.metadata.companyName} to Trello`);

    } catch (error) {
      console.error('Failed to send to Trello:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send to Trello';

      // Don't show UI error for rate limits or network issues
      if (!errorMessage.includes('rate') &&
          !errorMessage.includes('network') &&
          !errorMessage.includes('already been sent')) {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, isAuthenticated]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isAuthenticated,
    isLoading,
    isSent,
    error,
    sendToTrello,
    clearError
  };
};